<template>
  <skeleton-loader :loading="loading" type="sentences">
    <div class="race-headline" :style="`margin-bottom: ${presidential ? 10 : 20}px;`" v-if="model">
      Our model currently predicts that
      <span>
        <template v-if="presidential">
          <span :class="['bold', `${headline.partyClass}`]">
            {{ headline.party === 'Democrats' ? 'Joe Biden' : 'Donald Trump' }}
          </span>
        </template>
        <template v-else-if="candidates.length > 0">
          <span v-if="winningCand.candidacyType === 'INCUMBENT'">incumbent </span>
          <template v-if="byParty">the </template>
          <span :class="['bold', `${headline.partyClass}`]">
            {{ winningCand.name }}
            <template v-if="!byParty"> ({{ winningCand.party | initial }})</template>
          </span>
        </template>
        <template v-else> {{ headline.party }} </template>
      </span>
      {{((winningCand || presidential) && !byParty) ? 'has' : 'have' }}
      {{ getIndefiniteArticle(headline.chance) }}
      <span :class="`highlight-value ${headline.partyClass}`"> {{ headline.chance | votePercent }} </span>
      chance of {{ text }}<template v-if="!losingCand || presidential">.</template>
      <span v-if="losingCand && !presidential">
        over <span v-if="losingCand.candidacyType === 'INCUMBENT'">incumbent</span>
        <template v-if="byParty">the </template>
        <span :class="losingCand.party.substring(0, 3).toLowerCase()">
          {{ losingCand.name }}
          <template v-if="!byParty"> ({{ losingCand.party | initial }})</template>
          <span style="color: var(--text-color);">
            <template v-if="additionalCandidates.length === 0">.</template>
            <template v-if="showMoreCandidates">, </template>
          </span>
        </span>
        <span v-if="showMoreCandidates">
          <span v-for="(c, index) in additionalCandidates" :key="index">
            {{ c.name }} ({{ c.party | initial }})<template v-if="index + 1 < additionalCandidates.length">, </template><template v-else>.</template>
          </span>
        </span>
      </span>
      <span> {{ meanSeat }}</span>
      <!-- <span v-if="showTie && landingPage">
        There is {{ getIndefiniteArticle(tieProbability) }}
        <span class="highlight-value">{{ tieProbability | votePercent }}</span>
        chance of a tied (50-50) chamber.
      </span> -->
      <span v-if="model.election && additionalCandidates.length > 0" style="display: inline-block;">
        <v-btn depressed small v-if="!showMoreCandidates" @click="showMoreCandidates = true" class="ml-2">+{{ additionalCandidates.length }} more</v-btn>
      </span>
    </div>
    <div class="headline-footnote" v-if="presidential && landingPage">
      *There is a {{ chanceOfTie }}% chance of a 269-269 Electoral College tie.
    </div>
  </skeleton-loader>
</template>

<script>
import SkeletonLoader from '@/components/common/SkeletonLoader'

const props = {
  candidates: {
    type: Array,
    required: false,
    default: () => []
  },
  model: {
    required: true
  },
  prop: {
    type: String,
    required: true
  },
  landingPage: {
    type: Boolean,
    required: false
  },
  loading: {
    type: Boolean,
    required: true
  },
  presidential: {
    type: Boolean,
    required: false
  },
  text: {
    type: String,
    required: false,
    default: 'winning'
  },
  meanSeat: {
    type: String,
    default: "",
    required: true,
  },
}

const components = {
  SkeletonLoader
}

const computed = {
  additionalCandidates () {
    return this.model.election.electionJson.additionalCandidates
  },
  chanceOfTie () {
    const gopPct = (this.model[this.prop] * 100).toFixed(1)
    const demPct = (this.model['probabilityOfDemMajority'] * 100).toFixed(1)
    return (100 - gopPct - demPct).toFixed(1)
  },
  headline () {
    const gopWinning = this.model[this.prop] > 0.5
    if (gopWinning) {
      return {
        partyClass: 'gop',
        party: 'Republicans',
        chance: (this.model[this.prop] * 100).toFixed(1)
      }
    } else {
      if (this.prop === 'gopWinProbability') {
        return {
          partyClass: 'dem',
          party: 'Democrats',
          chance: (this.model['demWinProbability'] * 100).toFixed(1)
        }
      } else {
        return {
          partyClass: 'dem',
          party: 'Democrats',
          chance: (this.model['probabilityOfDemMajority'] * 100).toFixed(1)
        }
      }
    }
  },
  losingCand () {
    const winningParty = this.headline.party.substring(0, 3).toLowerCase()
    return this.candidates.find(c => !c.party.toLowerCase().startsWith(winningParty))
  },
  showTie() {
    return this.tieProbability > 5
  },
  tieProbability() {
    if (this.prop === 'gopWinProbability') {
      return (1 - (this.model['gopWinProbability'] + this.model['demWinProbability'])) * 100
    } else {
      return (1 - (this.model['probabilityOfGopMajority'] + this.model['probabilityOfDemMajority'])) * 100
    }
  },
  winningCand () {
    const winningParty = this.headline.party.substring(0, 3).toLowerCase()
    return this.candidates.find(c => c.party.toLowerCase().startsWith(winningParty))
  },
  byParty () {
    if (!this.winningCand) return
    return ['Democrats', 'Republicans'].includes(this.winningCand.name)
  }
}

const methods = {
  getIndefiniteArticle (value) {
    const case1 = value >= 8 && value < 9
    const case2 = value >= 11 && value < 12
    const case3 = value >= 80 && value < 90
    if (case1 || case2 || case3) {
      return ' an '
    }
    return ' a '
  }
}

export default {
  name: 'Headline',
  props,
  components,
  computed,
  methods,
  data: () => ({
    showMoreCandidates: false
  })
}
</script>

<style>
.race-headline {
  font-size: 22px;
  line-height: 28px;
}

.headline-footnote {
  font-size: 13px;
  opacity: 0.9;
  margin-bottom: 10px;
}
</style>
